import React, { useCallback, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import PATHS from '../../../../Routers/PATHS';
import { logoWordmark } from '../../../shared/assets';
import { NAV_LINKS } from '../../constants';
import UserMenu from './UserMenu';
import NotificationsBell from './NotificationsBell';

/**
 * The signed-in bar: the reader's account and notifications at one end, the
 * links centred, the logo at the other.
 *
 * From Figma node 16370:192161 — 72px tall, white, a hairline of gray/300
 * underneath, 64px of side padding. The links are 16px regular in slate/400,
 * 32px apart, and the one for the current page is lettered in the brand blue.
 *
 * ORDER, AND WHY IT LOOKS BACKWARDS
 *
 * The bar is right-to-left, so the first flex child lands at the right-hand
 * end. The logo is written first because the design puts it there, and the
 * account cluster last because the design puts it at the left. Reading the
 * JSX outside-in gives the Arabic layout directly; in English every pair
 * mirrors, which is the point of using flex order rather than absolute sides.
 *
 * WHERE THE LINKS GO
 *
 * Two of the four leave this module. The catalogue and the contact page are
 * landing routes that `StudentStack` now serves through `landingScreens`, so a
 * signed-in reader gets the rebuilt pages rather than the old ones — see the
 * note in that file. The subscriptions link goes through `studentScreens`,
 * which answers with the old page or the new one depending on
 * `STUDENT_SUBSCRIPTIONS`; `isV2StudentRoute` reads the same flag, so the page
 * and its chrome cannot disagree about which design is live.
 *
 * ONE MENU AT A TIME
 *
 * Which dropdown is down is state here, not inside each of them, because that
 * is the only arrangement in which they cannot both be open. The outside-click
 * test in `useDismissable` is no help on its own: a click inside the account
 * menu is, correctly, not outside it, so the bell would stay down underneath.
 *
 * The logo is an <img>, not `shared/Logo`: that component links to the public
 * home page, which is the wrong destination for someone already signed in. The
 * asset is the same file at the design's own 41px, a 96% scale of the 42.77px
 * the landing header draws it at, with the aspect ratio untouched.
 */
const StudentHeader = () => {
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const user = useSelector((state) => state.auth?.user);

    // Which dropdown is down, if either. One piece of state rather than a
    // boolean in each: it is the only way to guarantee they are never both
    // open, and opening one is then just naming it.
    const [openPanel, setOpenPanel] = useState(null);

    const closePanels = useCallback(() => setOpenPanel(null), []);
    const togglePanel = useCallback(
        (name) => setOpenPanel((current) => (current === name ? null : name)),
        []
    );

    // The dashboard would otherwise match nothing but itself and every link
    // would read as inactive on a sub-route; the landing links match by prefix
    // so a certificate's own page still lights "المناهج التعليمية".
    const isActive = (to) =>
        to === PATHS.USER_DASHBOARD.URL ? pathname === to : pathname.startsWith(to);

    // The `focus:`/`active:` repeats are not decoration. public/css/rsuite.min.css
    // is loaded globally from index.html and carries `a:focus, a:hover` and
    // `a:active` colour rules at (0,1,1); a plain Tailwind colour utility scores
    // (0,1,0) and loses, so a clicked link would keep rsuite's blue instead of
    // the brand's. Restating the colour under a variant lifts it back on top.
    // The same fix the landing NavBar carries, for the same stylesheet.
    const linkClasses = (to) =>
        isActive(to)
            ? 'tw-text-v2-brand focus:tw-text-v2-brand active:tw-text-v2-brand tw-font-bold'
            : 'tw-text-v2-student-nav hover:tw-text-v2-brand focus:tw-text-v2-brand active:tw-text-v2-brand tw-transition-colors';

    return (
        <header className="tw-sticky tw-top-0 tw-z-40 tw-bg-white tw-border-0 tw-border-b tw-border-solid tw-border-v2-ground-400">
            <div className="tw-relative tw-flex tw-h-[72px] tw-items-center tw-justify-between tw-gap-4 tw-px-[16px] lg:tw-px-[64px]">
                <Link
                    to={PATHS.USER_DASHBOARD.URL}
                    aria-label="Learning-Go Education"
                    className="tw-inline-flex tw-shrink-0 tw-items-center"
                >
                    <img
                        src={logoWordmark}
                        alt=""
                        aria-hidden
                        className="tw-h-[32px] tw-w-auto md:tw-h-[41px]"
                    />
                </Link>

                {/* Centred on the bar itself rather than between its
                    neighbours, so the links stay put as the reader's name
                    changes width. */}
                <nav
                    aria-label={t('v2.nav.primary')}
                    className="tw-absolute tw-start-1/2 tw--translate-x-1/2 rtl:tw-translate-x-1/2 tw-hidden lg:tw-flex tw-items-center tw-gap-[32px] tw-text-[16px] tw-leading-[1.6]"
                >
                    {NAV_LINKS.map((link) => (
                        <Link
                            key={link.key}
                            to={link.to}
                            aria-current={isActive(link.to) ? 'page' : undefined}
                            className={`${linkClasses(link.to)} tw-rounded focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-v2-brand focus-visible:tw-ring-offset-2`}
                        >
                            {t(link.labelKey)}
                        </Link>
                    ))}
                </nav>

                <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-[14px]">
                    <NotificationsBell
                        isOpen={openPanel === 'bell'}
                        onToggle={() => togglePanel('bell')}
                        onClose={closePanels}
                    />
                    <UserMenu
                        user={user}
                        isOpen={openPanel === 'menu'}
                        onToggle={() => togglePanel('menu')}
                        onClose={closePanels}
                    />
                </div>
            </div>

            {/* Below `lg` the centred nav is hidden — the logo, the name, the
                avatar and the bell already fill a phone. The links move to a
                strip of their own under the bar rather than into a burger: four
                short words scroll in one line, and a menu would be a second
                panel competing with the two the bar already opens. The strip
                still scrolls on a very narrow phone, but without a visible bar:
                on a 375px screen the words only just overflow, and the bar was
                the only thing drawn. */}
            <nav
                aria-label={t('v2.nav.primary')}
                className="lg:tw-hidden tw-flex tw-items-center tw-justify-between tw-gap-[16px] tw-overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:tw-hidden tw-border-0 tw-border-t tw-border-solid tw-border-v2-ground-400 tw-px-[16px] tw-py-[10px] tw-text-[14px]"
            >
                {NAV_LINKS.map((link) => (
                    <Link
                        key={link.key}
                        to={link.to}
                        aria-current={isActive(link.to) ? 'page' : undefined}
                        className={`${linkClasses(link.to)} tw-whitespace-nowrap`}
                    >
                        {t(link.labelKey)}
                    </Link>
                ))}
            </nav>
        </header>
    );
};

export default StudentHeader;
